"use client";

import * as React from "react";
import * as CollapsiblePrimitive from "@radix-ui/react-collapsible";
import { ChevronDown, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

const CollapsibleRoot = CollapsiblePrimitive.Root;

const CollapsibleTrigger = CollapsiblePrimitive.CollapsibleTrigger;

const CollapsibleContent = React.forwardRef<
  React.ElementRef<typeof CollapsiblePrimitive.CollapsibleContent>,
  React.ComponentPropsWithoutRef<typeof CollapsiblePrimitive.CollapsibleContent>
>(({ className, children, ...props }, ref) => (
  <CollapsiblePrimitive.CollapsibleContent
    ref={ref}
    className={`overflow-hidden transition-all duration-300 data-[state=closed]:max-h-0 data-[state=open]:max-h-[2000px] ${className || ""}`}
    {...props}
  >
    {children}
  </CollapsiblePrimitive.CollapsibleContent>
));
CollapsibleContent.displayName = CollapsiblePrimitive.CollapsibleContent.displayName;

export { CollapsibleRoot, CollapsibleTrigger, CollapsibleContent };

/**
 * Extract plain text from a React node for accessibility purposes.
 *
 * @param node - React node to extract text from
 * @returns Plain text string
 */
function extractTextFromNode(node: React.ReactNode): string {
  if (node == null || typeof node === "boolean") {
    return "";
  }
  if (typeof node === "string") {
    return node;
  }
  if (typeof node === "number") {
    return String(node);
  }
  if (Array.isArray(node)) {
    return node.map(extractTextFromNode).join(" ");
  }
  if (React.isValidElement(node)) {
    if ((node as React.ReactElement<{ children: React.ReactNode }>).props.children) {
      return extractTextFromNode((node as React.ReactElement<{ children: React.ReactNode }>).props.children);
    }
  }
  return "";
}

// Custom Collapsible component with built-in trigger and content
interface CollapsibleProps {
  title: React.ReactNode;
  ariaLabel?: string;
  description?: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
  className?: string;
  onOpenChange?: (open: boolean) => void;
}

export function Collapsible({
  title,
  ariaLabel,
  description,
  defaultOpen = false,
  children,
  className,
  onOpenChange,
}: CollapsibleProps) {
  const [isOpen, setIsOpen] = React.useState(defaultOpen);
  const contentId = React.useId();
  const titleId = React.useId();

  // Compute accessible label: use ariaLabel if provided, else extract from title
  const titleText = React.useMemo(() => {
    if (ariaLabel) {
      return ariaLabel;
    }
    if (typeof title === "string") {
      return title;
    }
    const extracted = extractTextFromNode(title);
    return extracted || "Collapsible section";
  }, [title, ariaLabel]);

  const handleToggle = () => {
    const newOpen = !isOpen;
    setIsOpen(newOpen);
    onOpenChange?.(newOpen);
  };

  return (
    <div className={cn("space-y-4", className)}>
      <button
        type="button"
        onClick={handleToggle}
        className="flex items-center justify-between w-full text-left"
        aria-expanded={isOpen}
        aria-controls={contentId}
        aria-label={titleText}
      >
        <div>
          <div id={titleId} className="text-base font-semibold">
            {title}
          </div>
          {description && <div className="text-sm text-muted-foreground">{description}</div>}
        </div>
        {isOpen ? (
          <ChevronDown className="h-5 w-5 shrink-0" aria-hidden="true" />
        ) : (
          <ChevronRight className="h-5 w-5 shrink-0" aria-hidden="true" />
        )}
      </button>

      {isOpen && (
        <div
          id={contentId}
          role="region"
          aria-labelledby={titleText}
          className="pl-4 border-l-2 animate-in slide-in-from-top-2 duration-200"
        >
          {children}
        </div>
      )}
    </div>
  );
}
